import { useEffect, useRef, useState, memo } from 'react';
import mermaid from 'mermaid';
import { Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
interface MermaidDiagramProps {
  chart: string;
}
// Initialize mermaid with configuration
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'loose',
  fontFamily: 'system-ui, -apple-system, sans-serif',
});
/**
 * MermaidDiagram Component
 * 渲染 Mermaid 图表
 */
/**
 * 🔥 使用 React.memo 包裹，防止父组件（MarkdownRenderer）re-render 时
 * 因 components 对象重建导致 MermaidDiagram 不必要的重新渲染。
 * chart 是 string 类型，memo 的浅比较会按值对比，
 * 只有内容真正变化时才会 re-render。
 */
const MermaidDiagram = memo(function MermaidDiagram({
  chart,
}: MermaidDiagramProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(chart);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy chart:', error);
    }
  };
  // 🔥 修复1：cancel flag + debounce 防止流式渲染时并发调用导致卡死
  // 🔥 修复2：锁定容器高度 + 跳过重复渲染，防止多个大图时无限闪动
  const cancelledRef = useRef(false);
  const lastRenderedChartRef = useRef<string>('');

  useEffect(() => {
    if (!containerRef.current) return;

    // 相同内容不重复渲染，避免布局抖动
    if (chart === lastRenderedChartRef.current) return;

    // 取消上一次渲染
    cancelledRef.current = true;

    const timer = setTimeout(() => {
      cancelledRef.current = false;

      const renderChart = async () => {
        try {
          setError(null);
          // 🔥 渲染前锁定容器高度，防止 innerHTML 更新时高度坍塌导致滚动抖动
          if (containerRef.current && containerRef.current.offsetHeight > 0) {
            containerRef.current.style.minHeight = `${containerRef.current.offsetHeight}px`;
          }
          const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
          const { svg } = await mermaid.render(id, chart);
          if (!cancelledRef.current && containerRef.current) {
            containerRef.current.innerHTML = svg;
            lastRenderedChartRef.current = chart;
            // 🔥 修复：渲染完成后将 minHeight 设为实际 SVG 高度，而非直接清除
            // 直接清除 minHeight 会导致容器高度瞬间从锁定值跳变到实际值，
            // 多个 Mermaid 图依次在 rAF 中释放高度时产生级联布局抖动，
            // 配合 scroll-container 的滚动监听形成 "回弹→滚动→回弹" 的闪动循环。
            // 设为实际高度可以避免任何高度跳变。
            requestAnimationFrame(() => {
              if (containerRef.current) {
                const actualHeight = containerRef.current.scrollHeight;
                containerRef.current.style.minHeight =
                  actualHeight > 0 ? `${actualHeight}px` : '';
              }
            });
          }
        } catch (err) {
          if (!cancelledRef.current) {
            console.error('Failed to render Mermaid chart:', err);
            setError(
              err instanceof Error
                ? err.message
                : t('mermaidDiagram.renderError')
            );
            // 出错时也释放高度锁定（错误场景直接清除即可，不会循环触发）
            if (containerRef.current) {
              containerRef.current.style.minHeight = '';
            }
          }
        }
      };
      renderChart();
    }, 300);

    return () => {
      cancelledRef.current = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);
  return (
    <div className="my-3 rounded-lg overflow-hidden border border-gray-300">
      {/* 标题栏 */}
      <div className="bg-gray-100 px-4 py-2 flex items-center justify-between border-b border-gray-300">
        <span className="text-xs font-semibold text-gray-600 uppercase">
          {t('mermaidDiagram.title')}
        </span>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors ${
            copied
              ? 'text-green-700 bg-green-100 hover:bg-green-200'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
          }`}
          title={copied ? t('common.copied') : t('mermaidDiagram.copyCode')}
        >
          {copied ? (
            <>
              <Check size={14} />
              <span>{t('common.copied')}</span>
            </>
          ) : (
            <>
              <Copy size={14} />
              <span>{t('common.copy')}</span>
            </>
          )}
        </button>
      </div>
      {/* 图表内容 */}
      <div className="bg-white p-6">
        {error ? (
          <div className="text-red-600 text-sm font-mono">
            <p className="font-semibold mb-2">
              {t('mermaidDiagram.failedToRender')}
            </p>
            <p>{error}</p>
            <pre className="mt-2 p-2 bg-red-50 rounded text-xs overflow-auto">
              {chart}
            </pre>
          </div>
        ) : (
          <div ref={containerRef} className="flex justify-center" />
        )}
      </div>
    </div>
  );
});

export default MermaidDiagram;
